import { Component, signal, computed, model, inject, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MeetingAttendanceStatusEnum, MeetingAttendanceStatusLabels } from 'src/app/enums/meeting-attendance-status.enum';
import { RegisterMeetingAttendanceItem, RegisterTripAttendance } from 'src/app/models/register-meeting-attendance-batch.interface';
import { ApiService } from 'src/app/services';
import { TripTransportTypeEnum } from 'src/app/enums/trip-transport-type.enum';
import { UserProfilePictureComponent } from 'src/app/components/utils/user-profile-picture/user-profile-picture.component';

import {
  PendingMeetingAttendance,
  PendingMeetingAttendanceUser,
  MeetingAttendanceData,
} from 'src/app/models/pending-meeting-attendance.interface';

type TripOption = number | TripTransportTypeEnum.PUBLIC_TRANSPORT | null;

@Component({
  selector: 'app-meeting-attendance-dialog',
  templateUrl: './meeting-attendance-dialog.component.html',
  styleUrl: './meeting-attendance-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatIcon,
    MatTooltip,
    MatFormField,
    MatLabel,
    MatHint,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatInput,
    UserProfilePictureComponent,
  ],
})
export class MeetingAttendanceDialogComponent {
  readonly dontKnowReasonMinLength: number = 20;
  readonly dontKnowReasonMaxLength: number = 255;

  selectedAttendance = model<MeetingAttendanceStatusEnum | null>(null);
  dontKnowReason = model('');

  selected_trip_option = model<TripOption>(null);

  currentMeetingIndex = signal(0);
  isSubmitting = signal(false);

  colleaguesAttendance = signal<ColleagueAttendanceMap>({});

  pendingMeetingAttendances = signal<PendingMeetingAttendance[]>([]);

  currentUserId: number;

  readonly MeetingAttendanceStatus = MeetingAttendanceStatusEnum;
  readonly MeetingAttendanceStatusLabels = MeetingAttendanceStatusLabels;
  readonly MeetingAttendanceStatusOptions = Object.values(MeetingAttendanceStatusEnum);

  readonly public_transport_option = TripTransportTypeEnum.PUBLIC_TRANSPORT;

  readonly currentMeeting = computed((): PendingMeetingAttendance | null => {
    const list = this.pendingMeetingAttendances();
    const index = this.currentMeetingIndex();
    return list.length > 0 && index >= 0 && index < list.length ? list[index] : null;
  });

  readonly workingTimeCategoryTooltip = computed((): string | null => {
    const meeting = this.currentMeeting();
    if (!meeting || meeting.title === meeting.working_time_category.name) {
      return null;
    }
    return meeting.working_time_category.name;
  });

  readonly currentCompanyProductColleaguesCount = computed((): number => {
    const meeting = this.currentMeeting();
    return meeting?.pending_users.length ?? 0;
  });

  readonly canSubmit = computed((): boolean => {
    const attendance = this.selectedAttendance();
    const hasNoAttendance = !attendance;

    if (hasNoAttendance) {
      return false;
    }

    const isDontKnowStatus = attendance === MeetingAttendanceStatusEnum.DONT_KNOW;
    const ownReason = this.dontKnowReason().trim();
    const hasInvalidOwnReason = ownReason.length < this.dontKnowReasonMinLength || ownReason.length > this.dontKnowReasonMaxLength;
    const isDontKnowWithoutReason = isDontKnowStatus && hasInvalidOwnReason;

    if (isDontKnowWithoutReason) {
      return false;
    }

    const isYesStatus = attendance === MeetingAttendanceStatusEnum.YES;
    const isYesButLateStatus = attendance === MeetingAttendanceStatusEnum.YES_BUT_LATE;
    const isAttendedStatus = isYesStatus || isYesButLateStatus;

    if (isAttendedStatus) {
      const currentMeeting = this.currentMeeting();

      if (!currentMeeting) {
        return false;
      }

      const hasPendingUsers = currentMeeting.pending_users.length > 0;

      if (hasPendingUsers) {
        const map = this.colleaguesAttendance();
        for (const colleague of currentMeeting.pending_users) {
          const colleagueAttendance = map[colleague.id]?.attendance;
          const hasNoColleagueAttendance = !colleagueAttendance;

          if (hasNoColleagueAttendance) {
            return false;
          }

          const isColleagueDontKnowStatus = colleagueAttendance === MeetingAttendanceStatusEnum.DONT_KNOW;
          const colleagueReason = map[colleague.id]?.dontKnowReason?.trim() ?? '';
          const hasInvalidColleagueReason =
            colleagueReason.length < this.dontKnowReasonMinLength || colleagueReason.length > this.dontKnowReasonMaxLength;
          const isColleagueDontKnowWithoutReason = isColleagueDontKnowStatus && hasInvalidColleagueReason;

          if (isColleagueDontKnowWithoutReason) {
            return false;
          }
        }
      }

      if (currentMeeting.is_trip && this.selected_trip_option() == null) {
        return false;
      }
    }

    return true;
  });

  readonly isLastMeeting = computed((): boolean => {
    const list = this.pendingMeetingAttendances();
    return list.length > 0 && this.currentMeetingIndex() === list.length - 1;
  });

  readonly currentColleagues = computed((): PendingMeetingAttendanceUser[] => {
    const meeting = this.currentMeeting();
    return meeting?.pending_users ?? [];
  });

  readonly shouldShowColleaguesSection = computed((): boolean => {
    const hasColleagues = this.currentCompanyProductColleaguesCount() > 0;
    const attendance = this.selectedAttendance();
    const isAttended = attendance === MeetingAttendanceStatusEnum.YES || attendance === MeetingAttendanceStatusEnum.YES_BUT_LATE;
    return hasColleagues && isAttended;
  });

  readonly is_trip_meeting = computed((): boolean => {
    return this.currentMeeting()?.is_trip ?? false;
  });

  readonly trip_participants = computed((): PendingMeetingAttendanceUser[] => {
    const candidates = this.currentMeeting()?.driver_candidates ?? [];
    const colleagues_attendance = this.colleaguesAttendance();

    return candidates.filter((candidate: PendingMeetingAttendanceUser): boolean => {
      if (candidate.id === this.currentUserId) {
        return true;
      }

      const colleague_attendance = colleagues_attendance[candidate.id]?.attendance;

      if (colleague_attendance == null) {
        return true;
      }

      return this.isAttendedStatus(colleague_attendance);
    });
  });

  readonly should_show_trip_section = computed((): boolean => {
    const attendance_status = this.selectedAttendance();
    const has_attended = this.isAttendedStatus(attendance_status);
    return this.is_trip_meeting() && has_attended;
  });

  readonly public_transport_label = computed((): string => {
    return this.trip_participants().length === 1 ? 'Nadie, fui en transporte público' : 'Nadie, fuimos en transporte público';
  });

  readonly selected_trip_option_label = computed((): string => {
    const selected_option = this.selected_trip_option();

    if (selected_option == null) {
      return '';
    }

    if (selected_option === TripTransportTypeEnum.PUBLIC_TRANSPORT) {
      return this.public_transport_label();
    }

    const participant = this.trip_participants().find((p: PendingMeetingAttendanceUser): boolean => p.id === selected_option);

    return participant ? `${participant.name} ${participant.surnames}` : '';
  });

  readonly isOwnDontKnowReasonInvalid = computed((): boolean => {
    if (this.selectedAttendance() !== MeetingAttendanceStatusEnum.DONT_KNOW) {
      return false;
    }
    const len = this.dontKnowReason().trim().length;
    return len > 0 && (len < this.dontKnowReasonMinLength || len > this.dontKnowReasonMaxLength);
  });

  readonly colleagueDontKnowReasonInvalidMap = computed((): Record<number, boolean> => {
    const map = this.colleaguesAttendance();
    const result: Record<number, boolean> = {};

    for (const colleagueId of Object.keys(map)) {
      const id = Number(colleagueId);
      const choice = map[id];
      if (!choice || choice.attendance !== MeetingAttendanceStatusEnum.DONT_KNOW) {
        result[id] = false;
      } else {
        const len = (choice.dontKnowReason ?? '').trim().length;
        result[id] = len > 0 && (len < this.dontKnowReasonMinLength || len > this.dontKnowReasonMaxLength);
      }
    }

    return result;
  });

  private readonly dialogRef = inject(MatDialogRef<MeetingAttendanceDialogComponent>);
  private readonly data = inject<MeetingAttendanceData>(MAT_DIALOG_DATA);
  private readonly api = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);

  constructor() {
    this.pendingMeetingAttendances.set(this.data.pendingMeetingAttendances);
    this.currentUserId = this.data.currentUserId;

    this.initializeCurrentMeetingColleagues();
  }

  submit(): void {
    const canSubmitForm = this.canSubmit();
    const hasCurrentMeeting = !!this.currentMeeting();
    const isFormValid = canSubmitForm && hasCurrentMeeting;

    if (!isFormValid) {
      return;
    }

    this.isSubmitting.set(true);

    const currentMeeting = this.currentMeeting();
    const selectedAttendance = this.selectedAttendance();
    const hasValidMeetingData = !!currentMeeting && !!selectedAttendance;

    if (!hasValidMeetingData) {
      this.isSubmitting.set(false);
      return;
    }

    const isDontKnowStatus = selectedAttendance === MeetingAttendanceStatusEnum.DONT_KNOW;
    if (isDontKnowStatus) {
      const ownReason = this.dontKnowReason().trim();
      if (ownReason.length < this.dontKnowReasonMinLength || ownReason.length > this.dontKnowReasonMaxLength) {
        this.isSubmitting.set(false);
        return;
      }
    }

    const attendances: RegisterMeetingAttendanceItem[] = [];

    const dontKnowReasonValue = isDontKnowStatus ? this.dontKnowReason().trim() : null;

    attendances.push({
      target_user_id: this.currentUserId,
      attendance_status: selectedAttendance,
      dont_know_reason: dontKnowReasonValue,
    });

    const isYesStatus = selectedAttendance === MeetingAttendanceStatusEnum.YES;
    const isYesButLateStatus = selectedAttendance === MeetingAttendanceStatusEnum.YES_BUT_LATE;
    const userAttended = isYesStatus || isYesButLateStatus;

    const shouldProcessColleagues = userAttended;

    if (shouldProcessColleagues) {
      const colleagues = this.currentColleagues();
      const map = this.colleaguesAttendance();
      for (const colleague of colleagues) {
        const colleagueAttendance = map[colleague.id];
        const hasColleagueAttendance = !!colleagueAttendance?.attendance;

        if (hasColleagueAttendance) {
          const status = colleagueAttendance.attendance;
          const isColleagueDontKnowStatus = status === MeetingAttendanceStatusEnum.DONT_KNOW;
          let colleagueDontKnowReason: string | null = null;
          if (isColleagueDontKnowStatus) {
            const colleagueReason = (colleagueAttendance.dontKnowReason ?? '').trim();
            if (colleagueReason.length < this.dontKnowReasonMinLength || colleagueReason.length > this.dontKnowReasonMaxLength) {
              this.isSubmitting.set(false);
              return;
            }
            colleagueDontKnowReason = colleagueReason;
          }

          attendances.push({
            target_user_id: colleague.id,
            attendance_status: status ?? MeetingAttendanceStatusEnum.NO,
            dont_know_reason: colleagueDontKnowReason,
          });
        }
      }
    }

    this.api
      .registerMeetingAttendanceBatch({
        employee_calendar_register_id: currentMeeting.id,
        attendances,
        trip: this.buildTripPayload(),
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: () => this.next(),
        error: (): void => {
          this.isSubmitting.set(false);
        },
      });
  }

  private buildTripPayload(): RegisterTripAttendance | null {
    if (!this.should_show_trip_section()) {
      return null;
    }

    const selected_option = this.selected_trip_option();

    if (selected_option == null) {
      return null;
    }

    const is_public_transport = selected_option === TripTransportTypeEnum.PUBLIC_TRANSPORT;

    return {
      trip_transport_type: is_public_transport ? TripTransportTypeEnum.PUBLIC_TRANSPORT : TripTransportTypeEnum.PRIVATE_VEHICLE,
      trip_driver_user_id: is_public_transport ? null : selected_option,
    };
  }

  setColleagueAttendance(colleagueId: number, attendance: MeetingAttendanceStatusEnum): void {
    this.colleaguesAttendance.update((map: ColleagueAttendanceMap): ColleagueAttendanceMap => {
      const current = map[colleagueId];

      const updated: ColleagueAttendanceChoice = {
        attendance,
        dontKnowReason: current?.dontKnowReason ?? '',
      };

      return { ...map, [colleagueId]: updated };
    });

    this.resetSelectedDriverIfUnavailable();
  }

  setColleagueDontKnowReason(colleagueId: number, dontKnowReason: string): void {
    this.colleaguesAttendance.update((map: ColleagueAttendanceMap): ColleagueAttendanceMap => {
      const current = map[colleagueId];

      if (current == null) {
        return map;
      }

      const updated: ColleagueAttendanceChoice = {
        ...current,
        dontKnowReason,
      };

      return { ...map, [colleagueId]: updated };
    });
  }

  private isAttendedStatus(attendance_status: MeetingAttendanceStatusEnum | null): boolean {
    return attendance_status === MeetingAttendanceStatusEnum.YES || attendance_status === MeetingAttendanceStatusEnum.YES_BUT_LATE;
  }

  private resetSelectedDriverIfUnavailable(): void {
    const selected_option = this.selected_trip_option();

    if (selected_option == null || selected_option === TripTransportTypeEnum.PUBLIC_TRANSPORT) {
      return;
    }

    const is_still_available = this.trip_participants().some(
      (participant: PendingMeetingAttendanceUser): boolean => participant.id === selected_option
    );

    if (!is_still_available) {
      this.selected_trip_option.set(null);
    }
  }

  private initializeCurrentMeetingColleagues(): void {
    const currentMeeting = this.currentMeeting();
    const initial: ColleagueAttendanceMap = {};
    (currentMeeting?.pending_users ?? []).forEach((colleague: PendingMeetingAttendanceUser): void => {
      initial[colleague.id] = {
        attendance: null,
        dontKnowReason: '',
      };
    });
    this.colleaguesAttendance.set(initial);
  }

  private next(): void {
    this.selectedAttendance.set(null);
    this.dontKnowReason.set('');
    this.selected_trip_option.set(null);

    const list = this.pendingMeetingAttendances();
    const currentIndex = this.currentMeetingIndex();
    const hasMoreMeetings = currentIndex < list.length - 1;

    if (hasMoreMeetings) {
      this.currentMeetingIndex.update((v: number): number => v + 1);
      this.initializeCurrentMeetingColleagues();
    } else {
      this.dialogRef.close();
    }

    this.isSubmitting.set(false);
  }
}

type ColleagueAttendanceChoice = {
  attendance: MeetingAttendanceStatusEnum | null;
  dontKnowReason: string;
};

type ColleagueAttendanceMap = Record<number, ColleagueAttendanceChoice>;
